import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Edit, Save, Eye, EyeOff, Calendar, Edit3, 
  Bold, Italic, Underline, List, ListOrdered, 
  ImageIcon, Video, Undo, Redo, Settings 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useManuals } from '../../contexts/ManualsContext';
import { formatDate } from '../../utils/dateHelpers';
import { MediaModal } from '../common/MediaModal';

export const ManualContent = () => {
  const { darkMode } = useTheme();
  const { userRole } = useAuth();
  const {
    manuals,
    activeCategory,
    activeManual,
    editorContent, setEditorContent,
    isEditMode, setIsEditMode,
    previewMode, setPreviewMode,
    saveContent,
    categories,
    setShowMediaModal
  } = useManuals();

  const editorRef = useRef(null);
  const manual = manuals[activeCategory]?.find(m => m.id === activeManual);

  useEffect(() => {
    if (isEditMode && editorRef.current) {
      editorRef.current.innerHTML = editorContent || '<p>Начните вводить содержимое здесь...</p>';
      editorRef.current.focus();
    }
  }, [isEditMode, editorContent]);

  const executeEditorCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    setEditorContent(editorRef.current.innerHTML);
  };

  const handleSave = () => {
    saveContent(activeCategory, activeManual, editorRef.current.innerHTML);
    setIsEditMode(false);
    setPreviewMode(true);
  };

  if (!activeManual || activeCategory === 'reports') return null;

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md overflow-hidden`}>
      {/* Заголовок и кнопки */}
      <div className={`border-b px-4 md:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
        darkMode ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div>
          <h1 className={`text-xl md:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {manual?.title || 'Руководство'}
          </h1>
          <p className={`text-xs md:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Раздел: {categories.find(c => c.id === activeCategory)?.name}
          </p>
        </div>
        {userRole === 'admin' && (
          <div className="flex flex-wrap gap-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleSave}
                  className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors touch-target ${
                    darkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm">Сохранить</span>
                </button>
                <button
                  onClick={() => {
                    setIsEditMode(false);
                    setPreviewMode(true);
                  }}
                  className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors touch-target ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">Предпросмотр</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditMode(true);
                    setPreviewMode(false);
                  }}
                  className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors touch-target ${
                    darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <Edit className="w-4 h-4" />
                  <span className="text-sm">Редактировать</span>
                </button>
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors touch-target ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {previewMode ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      <span className="text-sm">Исходный код</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">Предпросмотр</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Контент */}
      <div className="p-4 md:p-6">
        {isEditMode && userRole === 'admin' ? (
          // Режим редактирования
          <div className={`${darkMode ? 'border-gray-600' : 'border border-gray-300'} rounded-lg overflow-hidden`}>
            {/* Панель инструментов с горизонтальным скроллом на мобильных */}
            <div className={`${
              darkMode ? 'bg-gray-700 border-b border-gray-600' : 'bg-gray-100 border-b border-gray-300'
            } p-2 flex gap-1 overflow-x-auto whitespace-nowrap md:overflow-visible md:flex-wrap`}>
              <button
                onClick={() => executeEditorCommand('bold')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Жирный"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => executeEditorCommand('italic')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Курсив"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                onClick={() => executeEditorCommand('underline')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Подчеркивание"
              >
                <Underline className="w-4 h-4" />
              </button>
              <div className={`w-px h-6 mx-1 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              
              <button
                onClick={() => executeEditorCommand('insertOrderedList')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Нумерованный список"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              <button
                onClick={() => executeEditorCommand('insertUnorderedList')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Маркированный список"
              >
                <List className="w-4 h-4" />
              </button>
              <div className={`w-px h-6 mx-1 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              
              <button
                onClick={() => setShowMediaModal(true)}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-blue-400' : 'hover:bg-gray-200 text-blue-600'
                }`}
                title="Добавить медиа"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              <div className={`w-px h-6 mx-1 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
              
              <button
                onClick={() => executeEditorCommand('undo')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Отменить"
              >
                <Undo className="w-4 h-4" />
              </button>
              <button
                onClick={() => executeEditorCommand('redo')}
                className={`p-2 rounded transition-colors touch-target ${
                  darkMode ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-200'
                }`}
                title="Повторить"
              >
                <Redo className="w-4 h-4" />
              </button>
            </div>
            
            {/* Редактор */}
            <div
              ref={editorRef}
              contentEditable={true}
              className={`w-full min-h-[400px] md:min-h-[500px] p-4 outline-none ${
                darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'
              }`}
              onInput={(e) => setEditorContent(e.currentTarget.innerHTML)}
              suppressContentEditableWarning={true}
            />
          </div>
        ) : (
          // Режим просмотра
          <div className="prose prose-sm md:prose-lg max-w-none dark:prose-invert">
            {previewMode ? (
              <div
                className="content-preview break-words"
                dangerouslySetInnerHTML={{
                  __html: manual?.content || '<p class="text-gray-500 italic">Руководство находится в разработке</p>'
                }}
              />
            ) : (
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-lg font-mono text-sm overflow-x-auto`}>
                <pre className="whitespace-pre-wrap break-words">
                  {manual?.content || '// Руководство находится в разработке'}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Метаданные */}
        {manual && (
          <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm">
              <div className="flex items-center space-x-1">
                <Calendar className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Создано: {formatDate(manual.createdAt)}
                </span>
              </div>
              {manual.createdAt !== manual.updatedAt && (
                <div className="flex items-center space-x-1">
                  <Edit3 className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    Обновлено: {formatDate(manual.updatedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно добавления медиа */}
      <MediaModal />
    </div>
  );
};